import { useSyncExternalStore } from 'react';

/**
 * Which advisory firm THIS TAB is working as (steward-app#983).
 *
 * Only ever set for a caller the façade actually asked: `GET /advisor/firms` answers with
 * `firmChoice` instead of resolving a firm, the portal asks, and the answer is echoed back
 * as `?firmId=` on the four advisor reads. The usual reason for being asked is staff at two
 * registered firms; it is not the only one (see `AdvisorFirmChooser`).
 *
 * ⚠️ **NOT A GATE, NOT A DEFAULT, AND NOT AUTHORIZATION.** Read `useAdvisorFirm`'s header
 * first, then note that this value is weaker still: it is a SELECTION among firms the
 * SERVER computed from the caller's own memberships, and the server re-validates it on
 * every single request (`advisorFirmContext` — the field may only narrow that set, never
 * widen it). A forged or stale value cannot reach a firm the caller is not staff at; the
 * worst it does is resolve to nothing, at which point the façade asks again and this tab
 * re-renders the chooser.
 *
 * ## Per TAB, and that is the whole storage decision
 *
 * `sessionStorage`, not `localStorage`. Two consequences, both wanted:
 *
 *   - a NEW tab starts with no selection, so nothing is ever "sent by default" — a value
 *     is only ever here because a person in THIS tab picked it off a list the server
 *     offered. That is the rule steward-app#983 was decided under, and `localStorage` would
 *     break it by carrying a months-old pick into a session the server never asked;
 *   - an operator who genuinely works at two firms can have one open in each tab, which is
 *     the actual shape of the work docs/30 T6 describes (our own operator, at RIPL and at
 *     the internal demo firm) without any cross-tab coordination.
 *
 * It survives a reload of the same tab, which is the one continuity worth keeping: a
 * refresh must not throw away a choice the person just made.
 *
 * ⚠️ **Named residual: it also survives a SIGN-OUT in the same tab.** Nothing on the
 * sign-out path clears the `steward-app:*` keys — `steward-app:workspaceId` has always
 * behaved this way — so a second person signing in to that tab sends the first person's
 * selection. It costs them nothing: the facade intersects it with the NEW caller's own
 * memberships, so the outcome is either a firm they are genuinely staff at or the chooser.
 * Left alone deliberately rather than fixed here, because clearing it would mean importing
 * an advisor-portal module into the auth layer for a value that is already re-validated
 * server-side on every request (raised in this PR's security review as informational).
 *
 * The in-memory mirror below is not a cache for speed. It is what makes this readable at
 * all when storage throws — private browsing, a locked-down profile — where the selection
 * then lives for the life of the page and the portal still works.
 */
const ACTING_FIRM_KEY = 'steward-app:advisorActingFirmId';

/** `undefined` = storage not consulted yet; `null` = consulted, nothing chosen. */
let cached: string | null | undefined;

const listeners = new Set<() => void>();

function readStored(): string | null {
  try {
    const raw = sessionStorage.getItem(ACTING_FIRM_KEY);
    return raw && raw.trim() ? raw : null;
  } catch {
    // Storage unavailable (private browsing, blocked site data). Same swallow as
    // `rememberedFirmWorkspaceId` and `AdvisorLandingNudge`: this is a convenience, and a
    // portal that threw here would be worse than one that asks again.
    return null;
  }
}

/**
 * The firm id this tab is working as, or `null`.
 *
 * Callers append it to the advisor reads. Sending it when the server did NOT offer a choice
 * is harmless and deliberate: a single-firm advisor never has one stored (nothing ever
 * offered them a list), and if memberships change under a tab that does, the value stops
 * matching, the façade answers `firmChoice` again, and the chooser comes back. Self-healing
 * beats a client-side rule about when the parameter is "allowed".
 */
export function actingFirmId(): string | null {
  if (cached === undefined) cached = readStored();
  return cached;
}

/**
 * `?firmId=` for the firm a REACT QUERY KEY names, or the empty string.
 *
 * Shared by every advisor query factory so the parameter cannot drift into being sent on
 * one read and not another: an advisor whose Clients page is scoped to one firm and whose
 * Overview total is scoped to no firm at all would be worse than either alone. Six reads
 * as of steward-app#1015 — `GET /advisor/firms`, `/advisor/clients`, `/advisor/summary`,
 * `/advisor/shared-areas`, `/advisor/pending` and `/intakes` (`useFirmIntakes.ts`, whose
 * `drafts` half is firm-scoped server-side) — plus the ONE write that also carries it,
 * `POST /advisor/pending/{workspaceId}/activate`, where a missing parameter is refused
 * rather than guessed at.
 *
 * ⚠️ **It takes the key's OWN firm id rather than re-reading the store, and that is not a
 * style choice** (raised in this PR's code review). A `queryFn` runs at fetch time, not at
 * key-construction time, so a retry or a background refetch belonging to key
 * `[..., 'ripl']` could re-read a store that had since moved to `demo` and cache demo's
 * answer under ripl's key. That is one firm's data served to a session acting as another,
 * which is the whole failure steward-app#983 exists to close, reached through the back
 * door. Deriving both halves from the same key closes the window by construction.
 *
 * ⚠️ **It lives HERE, next to the store, and not in `useAdvisorFirm`** — every advisor page
 * suite replaces that module wholesale (`vi.mock('./useAdvisorFirm', () => ({ useAdvisorFirm }))`),
 * which would leave this function undefined inside `useAdvisorClients` and
 * `useAdvisorSummary` and take both pages down at render. The dependency belongs to the
 * store, not to the hook that happens to be the first caller.
 */
export function actingFirmQuery(keyFirmId: unknown): string {
  return typeof keyFirmId === 'string' && keyFirmId
    ? `?firmId=${encodeURIComponent(keyFirmId)}`
    : '';
}

function emit(): void {
  for (const l of listeners) l();
}

/** Record the firm this tab is working as. Called only from the chooser, which renders
 *  only when the façade asked. */
export function setActingFirmId(firmId: string): void {
  cached = firmId;
  try {
    sessionStorage.setItem(ACTING_FIRM_KEY, firmId);
  } catch {
    // See `readStored` — the in-memory mirror above still holds for this page.
  }
  emit();
}

/** Forget the selection, so the next read asks the façade again and the chooser returns.
 *  This is the whole of "change firm": there is no client-side list to re-render from, and
 *  deliberately so — the offered set is the server's answer, never a remembered one. */
export function clearActingFirmId(): void {
  cached = null;
  try {
    sessionStorage.removeItem(ACTING_FIRM_KEY);
  } catch {
    // See `readStored`.
  }
  emit();
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => { listeners.delete(onChange); };
}

/**
 * The selection as a React value, so a change re-keys the advisor queries and they refetch.
 *
 * `useSyncExternalStore` rather than context: every consumer is a query-options factory or
 * a leaf component, none of them share a subtree, and a provider would have to be mounted
 * app-wide for a value that matters on three routes.
 */
export function useActingFirmId(): string | null {
  return useSyncExternalStore(subscribe, actingFirmId, () => null);
}
